import { Button } from '@fruuit/ui'
import { PencilLine, Plus } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import {
  CreateDialogueDialog,
  CreateMilestoneDialog,
  CreateObjectiveDialog,
  EditEngagementDialog,
  useRecord,
} from '../records'

/**
 * Authoring adapter for the team case page (P-UI-001, R-09): each action renders its own
 * trigger button and mounts lane B's record dialog from `features/records`, calling
 * `onSaved` after a successful save so the case page can refetch.
 */
export interface AuthoringActionProps {
  engagementId: string
  /** CreateMilestone: the objective the milestone belongs to. */
  objectiveId?: string
  /** Current record revision, for edits (CAS). The dialog reads the live record itself. */
  revision?: number
  onSaved?: () => void
  size?: 'sm' | 'md'
}

interface TriggerProps {
  label: string
  kind: 'create' | 'edit'
  size: 'sm' | 'md'
  children: (open: boolean, close: () => void) => ReactNode
}

function AuthoringTrigger({ label, kind, size, children }: TriggerProps) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button
        variant="secondary"
        size={size}
        icon={kind === 'edit' ? <PencilLine /> : <Plus />}
        onClick={() => setOpen(true)}
      >
        {label}
      </Button>
      {children(open, () => setOpen(false))}
    </>
  )
}

function EditEngagementBody({
  engagementId,
  open,
  onClose,
  onSaved,
}: {
  engagementId: string
  open: boolean
  onClose: () => void
  onSaved?: () => void
}) {
  // Load the editable staff record only while the dialog is open.
  const { data } = useRecord('engagements', open ? engagementId : null)
  if (!data) return null
  return (
    <EditEngagementDialog
      open={open}
      onClose={onClose}
      engagement={data}
      onSaved={() => {
        onClose()
        onSaved?.()
      }}
    />
  )
}

export function EditEngagement({ engagementId, onSaved, size = 'sm' }: AuthoringActionProps) {
  return (
    <AuthoringTrigger label="Edit engagement" kind="edit" size={size}>
      {(open, close) => (
        <EditEngagementBody
          engagementId={engagementId}
          open={open}
          onClose={close}
          onSaved={onSaved}
        />
      )}
    </AuthoringTrigger>
  )
}

export function CreateObjective({ engagementId, onSaved, size = 'sm' }: AuthoringActionProps) {
  return (
    <AuthoringTrigger label="Add objective" kind="create" size={size}>
      {(open, close) => (
        <CreateObjectiveDialog
          open={open}
          onClose={close}
          engagementId={engagementId}
          onSaved={() => {
            close()
            onSaved?.()
          }}
        />
      )}
    </AuthoringTrigger>
  )
}

export function CreateMilestone({ objectiveId, onSaved, size = 'sm' }: AuthoringActionProps) {
  if (!objectiveId) return null
  return (
    <AuthoringTrigger label="Record evidence review" kind="create" size={size}>
      {(open, close) => (
        <CreateMilestoneDialog
          open={open}
          onClose={close}
          objectiveId={objectiveId}
          onSaved={() => {
            close()
            onSaved?.()
          }}
        />
      )}
    </AuthoringTrigger>
  )
}

export function CreateDialogue({ engagementId, onSaved, size = 'sm' }: AuthoringActionProps) {
  return (
    <AuthoringTrigger label="Add correspondence" kind="create" size={size}>
      {(open, close) => (
        <CreateDialogueDialog
          open={open}
          onClose={close}
          engagementId={engagementId}
          onSaved={() => {
            close()
            onSaved?.()
          }}
        />
      )}
    </AuthoringTrigger>
  )
}

/** False now that lane B's dialogs are connected (screens hide the "coming soon" note). */
export const AUTHORING_PENDING = false
